import type { TextItem } from 'pdfjs-dist/types/src/display/api'
import { compose, type Matrix, viewportTransform } from '@/lib/geometry'
import type { PageRef, Rect } from '@/types'
import { getSource } from './sources'

export interface TextRun {
  str: string
  /** box in base space (points) */
  x: number
  y: number
  w: number
  h: number
  /** baseline y in base space */
  baseline: number
  fontSize: number
  fontName: string
  generic: string
  /** text direction is horizontal (we only edit horizontal text) */
  horizontal: boolean
  hasEOL: boolean
}

export interface PageText {
  runs: TextRun[]
  /** all text joined; run i starts at offsets[i] */
  text: string
  offsets: number[]
}

const cache = new Map<string, Promise<PageText>>()
const key = (p: PageRef) => `${p.srcId}:${p.srcIndex}`

export function getPageText(page: PageRef): Promise<PageText> {
  if (!page.srcId) return Promise.resolve({ runs: [], text: '', offsets: [] })
  const k = key(page)
  let hit = cache.get(k)
  if (!hit) {
    hit = load(page)
    cache.set(k, hit)
    hit.catch(() => cache.delete(k))
  }
  return hit
}

export function clearTextCache() {
  cache.clear()
}

async function load(page: PageRef): Promise<PageText> {
  const src = getSource(page.srcId)
  if (!src) return { runs: [], text: '', offsets: [] }
  const p = await src.pdf.getPage(page.srcIndex + 1)
  const tc = await p.getTextContent({ includeMarkedContent: false, disableNormalization: false })
  const vt: Matrix = viewportTransform(page.view, page.baseRotation)
  const runs: TextRun[] = []
  for (const raw of tc.items) {
    const it = raw as TextItem
    if (typeof it.str !== 'string') continue
    const m = compose(it.transform as Matrix, vt)
    const fontHeight = Math.hypot(m[2], m[3])
    const angle = Math.atan2(m[1], m[0])
    const style = tc.styles[it.fontName]
    const ascent = style?.ascent ? style.ascent : style?.descent ? 1 + style.descent : 0.8
    const horizontal = Math.abs(angle) < 0.01
    // width/height in user units → base space (scale of the transform's x axis)
    const scaleX = Math.hypot(it.transform[0], it.transform[1]) || 1
    const w = (it.width / scaleX) * Math.hypot(m[0], m[1])
    const x = m[4]
    const baseline = m[5]
    const top = baseline - fontHeight * ascent
    runs.push({
      str: it.str,
      x: horizontal ? x : Math.min(x, x - fontHeight),
      y: top,
      w: horizontal ? w : fontHeight,
      h: horizontal ? fontHeight : w,
      baseline,
      fontSize: fontHeight,
      fontName: it.fontName,
      generic: style?.fontFamily ?? 'sans-serif',
      horizontal,
      hasEOL: it.hasEOL,
    })
  }
  let text = ''
  const offsets: number[] = []
  for (const r of runs) {
    offsets.push(text.length)
    text += r.str
    if (r.hasEOL) text += ' '
  }
  return { runs, text, offsets }
}

let measureCtx: CanvasRenderingContext2D | null = null
/** Width of a prefix of `str` as a fraction of the whole, using real glyph widths of a similar font. */
function fraction(r: TextRun, chars: number) {
  if (chars <= 0) return 0
  if (chars >= r.str.length) return 1
  if (typeof document === 'undefined') return chars / r.str.length
  measureCtx ??= document.createElement('canvas').getContext('2d')
  if (!measureCtx) return chars / r.str.length
  measureCtx.font = `100px ${r.generic || 'sans-serif'}`
  const total = measureCtx.measureText(r.str).width
  return total ? measureCtx.measureText(r.str.slice(0, chars)).width / total : chars / r.str.length
}

/** Rectangles covering characters [start, end) of the joined page text. */
export function rangeRects(pt: PageText, start: number, end: number): Rect[] {
  const out: Rect[] = []
  for (let i = 0; i < pt.runs.length; i++) {
    const r = pt.runs[i]
    const s = pt.offsets[i]
    const e = s + r.str.length
    if (e <= start || s >= end || !r.str.length) continue
    const a = Math.max(start, s) - s
    const b = Math.min(end, e) - s
    const fa = fraction(r, a)
    const fb = fraction(r, b)
    if (r.horizontal) out.push({ x: r.x + r.w * fa, y: r.y, w: r.w * (fb - fa), h: r.h })
    else out.push({ x: r.x, y: r.y + r.h * fa, w: r.w, h: r.h * (fb - fa) })
  }
  return out
}

/**
 * Group pdf.js text items into editable lines: adjacent items on the same baseline with the
 * same font size are merged (pdf.js often splits a single line into many fragments).
 */
export function editableLines(pt: PageText): TextRun[] {
  const lines: TextRun[] = []
  for (const r of pt.runs) {
    if (!r.str.trim() || !r.horizontal) continue
    const last = lines.at(-1)
    const sameLine =
      last &&
      Math.abs(last.baseline - r.baseline) < r.fontSize * 0.2 &&
      Math.abs(last.fontSize - r.fontSize) < 0.6 &&
      r.x >= last.x + last.w - r.fontSize * 0.5 &&
      r.x - (last.x + last.w) < r.fontSize * 1.2
    if (last && sameLine) {
      const gap = r.x - (last.x + last.w)
      const needsSpace = gap > r.fontSize * 0.15 && !last.str.endsWith(' ') && !r.str.startsWith(' ')
      const right = Math.max(last.x + last.w, r.x + r.w)
      last.str += (needsSpace ? ' ' : '') + r.str
      last.y = Math.min(last.y, r.y)
      last.h = Math.max(last.h, r.h)
      last.w = right - last.x
    } else lines.push({ ...r })
  }
  return lines
}

export interface SearchMatch {
  pageIndex: number
  pageId: string
  rects: Rect[]
  preview: string
}

function escapeRe(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export async function searchPages(
  pages: PageRef[],
  query: string,
  opts: { caseSensitive?: boolean; wholeWord?: boolean },
  signal: AbortSignal,
  onMatches: (m: SearchMatch[]) => void,
) {
  const q = query.trim()
  if (!q) return
  const flags = opts.caseSensitive ? 'g' : 'gi'
  const body = escapeRe(q).replace(/\s+/g, '\\s+')
  const re = new RegExp(opts.wholeWord ? `\\b${body}\\b` : body, flags)
  for (let i = 0; i < pages.length; i++) {
    if (signal.aborted) return
    const pt = await getPageText(pages[i])
    const found: SearchMatch[] = []
    re.lastIndex = 0
    for (let m = re.exec(pt.text); m; m = re.exec(pt.text)) {
      if (!m[0].length) {
        re.lastIndex++
        continue
      }
      const s = m.index
      const e = s + m[0].length
      found.push({
        pageIndex: i,
        pageId: pages[i].id,
        rects: rangeRects(pt, s, e),
        preview: `${s > 30 ? '…' : ''}${pt.text.slice(Math.max(0, s - 30), Math.min(pt.text.length, e + 40))}`,
      })
    }
    if (found.length) onMatches(found)
  }
}
